// Re-encodes a HEIC/HEIF file as JPEG entirely client-side, via an <img>/<canvas> round trip.
// Duncan's OpenCV pipeline has no HEIC decoder, so a HEIC evidence photo uploads fine but fails
// downstream at citation-push time (AUT-5840 investigation) -- this must run before the file ever
// leaves the browser.
//
// The decode step only works in WebKit: HEIC decode support comes from the OS's native image
// framework (ImageIO on Apple platforms), not from anything in the HTML/Canvas spec, so Blink and
// Gecko can't decode HEIC on any platform. In practice that's fine here -- every browser on an
// iPhone is WebKit under Apple's engine mandate, and that's the realistic officer device. It
// rejects if the source can't be decoded (e.g. a genuine HEIC file opened in a non-WebKit
// browser) or the canvas fails to produce a blob, so the caller can surface a clear error instead
// of silently uploading unconverted HEIC bytes.
export async function convertHeicToJpeg(file: File): Promise<File> {
  const objectUrl = URL.createObjectURL(file);
  try {
    const image = await loadImage(objectUrl);
    const canvas = document.createElement("canvas");
    canvas.width = image.naturalWidth;
    canvas.height = image.naturalHeight;
    const context = canvas.getContext("2d");
    if (!context) {
      throw new Error("Canvas 2D context is unavailable");
    }
    // Baking the decoded pixels into the canvas (rather than copying EXIF metadata onto the
    // output) is what keeps orientation correct here -- WebKit's decoder already applies EXIF
    // rotation when drawing the source image, so the canvas's own pixels are already right-side
    // up with no orientation tag needed downstream.
    context.drawImage(image, 0, 0);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.92));
    if (!blob) {
      throw new Error("Canvas failed to produce a JPEG blob");
    }
    return new File([blob], toJpegFileName(file.name), { type: "image/jpeg" });
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Failed to decode image"));
    image.src = src;
  });
}

function toJpegFileName(fileName: string): string {
  return `${fileName.replace(/\.(heic|heif)$/i, "")}.jpg`;
}

export default convertHeicToJpeg;
